// pages/HomeScreenWithForms.js - Cleaned up version without API key logic
import React, { useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { CSSTransition, SwitchTransition } from 'react-transition-group';
import TonicLogo from '../../../assets/logos/tonicLogos/TonicLogo.png';
import TonicIcon from '../../../assets/icons/tonicIcons/TonicIcon.png';
import AuthBackground from '../../../assets/backgrounds/AuthBackground.svg';
import SliderArrow from '../../../assets/components/auth/SliderArrow.svg';
import LoginForm from './forms/LoginForm';
import OTPForm from './forms/OTPForm';
import PostLoginLoader from '../components/PostLoginLoader';
import { asyncStorage } from '../../../shared/services/storage/asyncStorageService';

const FormType = {
  LOGIN: 'login',
  OTP: 'otp',
};

const HomeScreenWithForms = () => {
  const [isSliderExpanded, setIsSliderExpanded] = useState(false);
  const [formType, setFormType] = useState(FormType.LOGIN);
  const [showForm, setShowForm] = useState(false);
  const [showPostLoginLoader, setShowPostLoginLoader] = useState(false);
  const [userData, setUserData] = useState({
    email: '',
  });
  const [microsoftError, setMicrosoftError] = useState('');

  // Ref for CSSTransition to avoid findDOMNode deprecation warning
  const nodeRef = useRef(null);

  useEffect(() => {

    // Check for callback URL and do nothing if we're processing
    if (window.location.search.includes('code=')) {
      return;
    }

    // CRITICAL: Clear stale userData from localStorage when landing on login page
    // This prevents pre-login API calls from OrganizationPreloader and other hooks
    // that read userData from localStorage
    asyncStorage.remove('userData');
    sessionStorage.clear();

    // Handle Microsoft errors from URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    const msFailed = urlParams.get('ms_failed') || urlParams.get('microsoft_failed');
    const googleFailed = urlParams.get('google_failed');

    if (msFailed === 'true') {
      sessionStorage.setItem('ms_auth_failed', 'true');
      sessionStorage.setItem('ms_error_message', 'Microsoft authentication failed. Please try again.');
      window.history.replaceState({}, document.title, window.location.pathname);
      setMicrosoftError('Microsoft authentication failed. Please try again.');
      setFormType(FormType.LOGIN);
    }

    if (googleFailed === 'true') {
      sessionStorage.setItem('google_auth_failed', 'true');
      sessionStorage.setItem('google_error_message', 'Google authentication failed. Please try again.');
      window.history.replaceState({}, document.title, window.location.pathname);
      setFormType(FormType.LOGIN);
    }

    // Clean up any restart flags when component loads
    sessionStorage.removeItem('restart_ms_auth_after_logout');
    sessionStorage.removeItem('ms_auth_email_retry');

  }, []);

  // Auto-expand slider on mount (removed API key dependency)
  useEffect(() => {
    const timer = setTimeout(() => {
      toggleSlider();
    }, 500);
    return () => clearTimeout(timer);
  }, []);

  // Handle slider toggle with animation sequencing
  const toggleSlider = () => {
    if (isSliderExpanded) {
      setShowForm(false);
      setTimeout(() => {
        setIsSliderExpanded(false);
      }, 450);
    } else {
      setIsSliderExpanded(true);
      setTimeout(() => {
        setShowForm(true);
      }, 450);
    }
  };

  const handleLogin = (formData) => {
    setUserData({ email: formData.email });
    setFormType(FormType.OTP);
  };

  const renderForm = () => {
    switch (formType) {
      case FormType.LOGIN:
        return (
          <LoginForm
            onSubmit={handleLogin}
            initialError={microsoftError}
          />
        );
      case FormType.OTP:
        return (
          <OTPForm
            onSwitchToLogin={() => setFormType(FormType.LOGIN)}
            email={userData.email}
            onVerified={() => setShowPostLoginLoader(true)}
          />
        );
      default:
        return <LoginForm onSubmit={handleLogin} initialError={microsoftError} />;
    }
  };

  if (showPostLoginLoader) return <PostLoginLoader />;

  return (
    <div className="HomeScreen-container">
      {/* Background SVG */}
      <div className={`HomeScreen-background ${isSliderExpanded ? 'HomeScreen-blur' : ''}`}>
        <img src={AuthBackground} alt="Background" />
      </div>

      {/* Main Wrapper for Icon and Logo */}
      <div className={`HomeScreen-main-wrapper ${isSliderExpanded ? 'HomeScreen-shifted' : ''}`}>
        <div className={`HomeScreen-content-container ${isSliderExpanded ? 'HomeScreen-blur' : ''}`}>
          <div className="HomeScreen-icon-container">
            <img src={TonicIcon} alt="Icon" />
          </div>
          <div className="HomeScreen-logo-container">
            <img src={TonicLogo} alt="Logo" />
          </div>
        </div>
      </div>

      {/* Fixed-size Slider with Tab */}
      <div className="HomeScreen-slider-wrapper">
        {/* Slider with clipped view when collapsed */}
        <div className={`HomeScreen-slider ${isSliderExpanded ? 'HomeScreen-slider-expanded' : ''}`}>
          <div className="HomeScreen-slider-svg-container">
            <svg 
              xmlns="http://www.w3.org/2000/svg" 
              viewBox="0 0 1036 1080" 
              fill="none"
              className="HomeScreen-slider-svg"
              preserveAspectRatio="none"
            >
              <g filter="url(#filter0_d_1_1725)">
                <path 
                  d="M88.5 338.445V-18C88.5 -40.0914 106.409 -58 128.5 -58H1012.5C1034.59 -58 1052.5 -40.0914 1052.5 -18V1099C1052.5 1121.09 1034.59 1139 1012.5 1139H128.5C106.409 1139 88.5 1121.09 88.5 1099V741.555C88.5 728.529 81.5405 716.496 70.25 710C58.9595 703.504 52 691.471 52 678.445V401.555C52 388.529 58.9595 376.496 70.25 370C81.5405 363.504 88.5 351.471 88.5 338.445Z" 
                  fill="#282B32"
                  className="HomeScreen-slider-path"
                />
              </g>
              <defs>
                <filter id="filter0_d_1_1725" x="0" y="-98" width="1080.5" height="1277" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
                  <feFlood floodOpacity="0" result="BackgroundImageFix"/>
                  <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
                  <feOffset dx="-12"/>
                  <feGaussianBlur stdDeviation="20"/>
                  <feComposite in2="hardAlpha" operator="out"/>
                  <feColorMatrix type="matrix" values="0 0 0 0 0.580392 0 0 0 0 0.0156863 0 0 0 0 0.764706 0 0 0 0.6 0"/>
                  <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_1_1725"/>
                  <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_1_1725" result="shape"/>
                </filter>
              </defs>
            </svg>
          </div>
          
          {/* Card SVG with Form Content */}
          <div className={`HomeScreen-slider-content ${showForm ? 'slider-content-visible' : ''}`}>
            <div className="HomeScreen-card-container">
              {/* Card SVG Shell */}
              <div className="HomeScreen-card-shell">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 667 858" fill="none" className="HomeScreen-card-svg">
                  <g filter="url(#filter0_ddddii_1_1727)">
                    <path d="M50 30H596.767C603.915 30 610.52 33.8154 614.092 40.0079L629.325 66.4203C631.078 69.4588 632 72.9048 632 76.4124V802.5C632 813.546 623.046 822.5 612 822.5H65.2332C58.0846 822.5 51.4795 818.685 47.9081 812.492L32.6749 786.08C30.9225 783.041 30 779.595 30 776.088V50C30 38.9543 38.9543 30 50 30Z" fill="#282B32"/>
                  </g>
                  <defs>
                    <filter id="filter0_ddddii_1_1727" x="0" y="0" width="667" height="857.5" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
                      <feFlood floodOpacity="0" result="BackgroundImageFix"/>
                      <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
                      <feOffset dx="10" dy="10"/>
                      <feGaussianBlur stdDeviation="12.5"/>
                      <feColorMatrix type="matrix" values="0 0 0 0 0.0705882 0 0 0 0 0.0862745 0 0 0 0 0.12549 0 0 0 0.9 0"/>
                      <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_1_1727"/>
                      <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
                      <feOffset dx="-10" dy="-10"/>
                      <feGaussianBlur stdDeviation="10"/>
                      <feColorMatrix type="matrix" values="0 0 0 0 0.223333 0 0 0 0 0.239091 0 0 0 0 0.266667 0 0 0 0.9 0"/>
                      <feBlend mode="normal" in2="effect1_dropShadow_1_1727" result="effect2_dropShadow_1_1727"/>
                      <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
                      <feOffset dx="10" dy="-10"/>
                      <feGaussianBlur stdDeviation="10"/>
                      <feColorMatrix type="matrix" values="0 0 0 0 0.0705882 0 0 0 0 0.0862745 0 0 0 0 0.12549 0 0 0 0.2 0"/>
                      <feBlend mode="normal" in2="effect2_dropShadow_1_1727" result="effect3_dropShadow_1_1727"/>
                      <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
                      <feOffset dx="-10" dy="10"/>
                      <feGaussianBlur stdDeviation="10"/>
                      <feColorMatrix type="matrix" values="0 0 0 0 0.0708333 0 0 0 0 0.0843749 0 0 0 0 0.125 0 0 0 0.2 0"/>
                      <feBlend mode="normal" in2="effect3_dropShadow_1_1727" result="effect4_dropShadow_1_1727"/>
                      <feBlend mode="normal" in="SourceGraphic" in2="effect4_dropShadow_1_1727" result="shape"/>
                      <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
                      <feOffset dx="-1" dy="-1"/>
                      <feGaussianBlur stdDeviation="1"/>
                      <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1"/>
                      <feColorMatrix type="matrix" values="0 0 0 0 0.0705882 0 0 0 0 0.0862745 0 0 0 0 0.12549 0 0 0 0.5 0"/>
                      <feBlend mode="normal" in2="shape" result="effect5_innerShadow_1_1727"/>
                      <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
                      <feOffset dx="1" dy="1"/>
                      <feGaussianBlur stdDeviation="1"/>
                      <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1"/>
                      <feColorMatrix type="matrix" values="0 0 0 0 0.223529 0 0 0 0 0.239216 0 0 0 0 0.266667 0 0 0 0.3 0"/>
                      <feBlend mode="normal" in2="effect5_innerShadow_1_1727" result="effect6_innerShadow_1_1727"/>
                    </filter>
                  </defs>
                </svg>
              </div>
              
              {/* Form Content with Transitions */}
              <div className="HomeScreen-card-content">
                <SwitchTransition mode="out-in">
                  <CSSTransition
                    key={formType}
                    nodeRef={nodeRef}
                    addEndListener={(done) => {
                      nodeRef.current?.addEventListener("transitionend", done, false);
                    }}
                    classNames="form-fade"
                  >
                    <div ref={nodeRef} style={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
                      {renderForm()}
                    </div>
                  </CSSTransition>
                </SwitchTransition>
              </div>
            </div>
          </div>
        </div>
        
        {/* Tab Arrow */}
        <div className="HomeScreen-tab-arrow" onClick={toggleSlider}>
          <img 
            src={SliderArrow} 
            alt="Slider Arrow" 
            className={`HomeScreen-arrow-icon ${isSliderExpanded ? 'HomeScreen-arrow-rotated' : ''}`}
          />
        </div>
      </div>
    </div>
  );
};

HomeScreenWithForms.propTypes = {};

export default HomeScreenWithForms;